import React, { useState, useEffect } from 'react';
import ReactFlagsSelect from 'react-flags-select';

// Convert ISO country code to emoji flag
const getEmojiFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '';
  const code = countryCode.toUpperCase();
  // Convert letters to regional indicator symbols
  const base = 127397; // Regional indicator symbol letter A is 0x1F1E6 = 127462, but we use offset from 'A' = 65
  // Actually: Regional indicator A is 0x1F1E6 = 127462
  // So for 'A' (65), we need 127462, which is 127397 offset
  const first = code.charCodeAt(0);
  const second = code.charCodeAt(1);
  return String.fromCodePoint(first + 127397) + String.fromCodePoint(second + 127397);
};

// All countries map - ISO code to country name
const countryCodeMap = {
  AD: 'Andorra', AE: 'United Arab Emirates', AF: 'Afghanistan', AG: 'Antigua and Barbuda', AI: 'Anguilla',
  AL: 'Albania', AM: 'Armenia', AO: 'Angola', AQ: 'Antarctica', AR: 'Argentina',
  AS: 'American Samoa', AT: 'Austria', AU: 'Australia', AW: 'Aruba', AX: 'Aland Islands',
  AZ: 'Azerbaijan', BA: 'Bosnia and Herzegovina', BB: 'Barbados', BD: 'Bangladesh', BE: 'Belgium',
  BF: 'Burkina Faso', BG: 'Bulgaria', BH: 'Bahrain', BI: 'Burundi', BJ: 'Benin',
  BL: 'Saint Barthelemy', BM: 'Bermuda', BN: 'Brunei', BO: 'Bolivia', BQ: 'Bonaire, Sint Eustatius and Saba',
  BR: 'Brazil', BS: 'Bahamas', BT: 'Bhutan', BV: 'Bouvet Island', BW: 'Botswana',
  BY: 'Belarus', BZ: 'Belize', CA: 'Canada', CC: 'Cocos Islands', CD: 'Congo, Democratic Republic',
  CF: 'Central African Republic', CG: 'Congo', CH: 'Switzerland', CI: "Cote D'Ivoire", CK: 'Cook Islands',
  CL: 'Chile', CM: 'Cameroon', CN: 'China', CO: 'Colombia', CR: 'Costa Rica',
  CU: 'Cuba', CV: 'Cape Verde', CW: 'Curacao', CX: 'Christmas Island', CY: 'Cyprus',
  CZ: 'Czech Republic', DE: 'Germany', DJ: 'Djibouti', DK: 'Denmark', DM: 'Dominica',
  DO: 'Dominican Republic', DZ: 'Algeria', EC: 'Ecuador', EE: 'Estonia', EG: 'Egypt',
  EH: 'Western Sahara', ER: 'Eritrea', ES: 'Spain', ET: 'Ethiopia', FI: 'Finland',
  FJ: 'Fiji', FK: 'Falkland Islands', FM: 'Micronesia', FO: 'Faroe Islands', FR: 'France',
  GA: 'Gabon', GB: 'United Kingdom', GD: 'Grenada', GE: 'Georgia', GF: 'French Guiana',
  GG: 'Guernsey', GH: 'Ghana', GI: 'Gibraltar', GL: 'Greenland', GM: 'Gambia',
  GN: 'Guinea', GP: 'Guadeloupe', GQ: 'Equatorial Guinea', GR: 'Greece', GS: 'South Georgia',
  GT: 'Guatemala', GU: 'Guam', GW: 'Guinea-Bissau', GY: 'Guyana', HK: 'Hong Kong',
  HM: 'Heard Island', HN: 'Honduras', HR: 'Croatia', HT: 'Haiti', HU: 'Hungary',
  ID: 'Indonesia', IE: 'Ireland', IL: 'Israel', IM: 'Isle of Man', IN: 'India',
  IO: 'British Indian Ocean Territory', IQ: 'Iraq', IR: 'Iran', IS: 'Iceland', IT: 'Italy',
  JE: 'Jersey', JM: 'Jamaica', JO: 'Jordan', JP: 'Japan', KE: 'Kenya',
  KG: 'Kyrgyzstan', KH: 'Cambodia', KI: 'Kiribati', KM: 'Comoros', KN: 'Saint Kitts',
  KP: 'North Korea', KR: 'Korea, South', KW: 'Kuwait', KY: 'Cayman Islands', KZ: 'Kazakhstan',
  LA: 'Laos', LB: 'Lebanon', LC: 'Saint Lucia', LI: 'Liechtenstein', LK: 'Sri Lanka',
  LR: 'Liberia', LS: 'Lesotho', LT: 'Lithuania', LU: 'Luxembourg', LV: 'Latvia',
  LY: 'Libya', MA: 'Morocco', MC: 'Monaco', MD: 'Moldova', ME: 'Montenegro',
  MF: 'Saint Martin', MG: 'Madagascar', MH: 'Marshall Islands', MK: 'Macedonia', ML: 'Mali',
  MM: 'Myanmar', MN: 'Mongolia', MO: 'Macao', MP: 'Northern Mariana Islands', MQ: 'Martinique',
  MR: 'Mauritania', MS: 'Montserrat', MT: 'Malta', MU: 'Mauritius', MV: 'Maldives',
  MW: 'Malawi', MX: 'Mexico', MY: 'Malaysia', MZ: 'Mozambique', NA: 'Namibia',
  NC: 'New Caledonia', NE: 'Niger', NF: 'Norfolk Island', NG: 'Nigeria', NI: 'Nicaragua',
  NL: 'Netherlands', NO: 'Norway', NP: 'Nepal', NR: 'Nauru', NU: 'Niue',
  NZ: 'New Zealand', OM: 'Oman', PA: 'Panama', PE: 'Peru', PF: 'French Polynesia',
  PG: 'Papua New Guinea', PH: 'Philippines', PK: 'Pakistan', PL: 'Poland', PM: 'Saint Pierre',
  PN: 'Pitcairn', PR: 'Puerto Rico', PS: 'Palestine', PT: 'Portugal', PW: 'Palau',
  PY: 'Paraguay', QA: 'Qatar', RE: 'Reunion', RO: 'Romania', RS: 'Serbia',
  RU: 'Russia', RW: 'Rwanda', SA: 'Saudi Arabia', SB: 'Solomon Islands', SC: 'Seychelles',
  SD: 'Sudan', SE: 'Sweden', SG: 'Singapore', SH: 'Saint Helena', SI: 'Slovenia',
  SJ: 'Svalbard', SK: 'Slovakia', SL: 'Sierra Leone', SM: 'San Marino', SN: 'Senegal',
  SO: 'Somalia', SR: 'Suriname', SS: 'South Sudan', ST: 'Sao Tome', SV: 'El Salvador',
  SX: 'Sint Maarten', SY: 'Syria', SZ: 'Swaziland', TC: 'Turks and Caicos', TD: 'Chad',
  TF: 'French Southern Territories', TG: 'Togo', TH: 'Thailand', TJ: 'Tajikistan', TK: 'Tokelau',
  TL: 'Timor-Leste', TM: 'Turkmenistan', TN: 'Tunisia', TO: 'Tonga', TR: 'Turkey',
  TT: 'Trinidad and Tobago', TV: 'Tuvalu', TW: 'Taiwan', TZ: 'Tanzania', UA: 'Ukraine',
  UG: 'Uganda', UM: 'United States Minor Outlying Islands', US: 'United States', UY: 'Uruguay',
  UZ: 'Uzbekistan', VA: 'Holy See', VC: 'Saint Vincent', VE: 'Venezuela', VG: 'Virgin Islands, British',
  VI: 'Virgin Islands, U.S.', VN: 'Vietnam', VU: 'Vanuatu', WF: 'Wallis and Futuna', WS: 'Samoa',
  YE: 'Yemen', YT: 'Mayotte', ZA: 'South Africa', ZM: 'Zambia', ZW: 'Zimbabwe'
};

// Create reverse mapping: country name to ISO code (lowercase for case-insensitive lookup)
const countryNameToCodeMap = {};
Object.entries(countryCodeMap).forEach(([code, name]) => {
  countryNameToCodeMap[name.toLowerCase()] = code;
});

// Helper function to find ISO code by country name (case-insensitive)
const getISOFromCountryName = (countryName) => {
  if (!countryName) return '';
  const normalizedName = countryName.trim().toLowerCase();
  return countryNameToCodeMap[normalizedName] || '';
};

// Convert emoji flag back to ISO code
const getISOFromEmoji = (emoji) => {
  if (!emoji || emoji.length !== 2) return '';
  // Check if it's an emoji flag (regional indicator symbols)
  const code1 = emoji.codePointAt(0);
  const code2 = emoji.codePointAt(1);
  // Regional indicator symbols range: 0x1F1E6 to 0x1F1FF (127462 to 127487)
  if (code1 >= 127462 && code1 <= 127487 && code2 >= 127462 && code2 <= 127487) {
    const char1 = String.fromCharCode(code1 - 127397);
    const char2 = String.fromCharCode(code2 - 127397);
    return char1 + char2;
  }
  return '';
};

const FlagSelector = ({ value, onChange, onSelect, countryName }) => {
  const [selected, setSelected] = useState('');

  // Get ISO code from value (emoji flag or ISO code)
  const getISOFromValue = (val) => {
    if (!val) return '';
    // If it's already a 2-letter code, return it
    if (val.length === 2 && countryCodeMap[val.toUpperCase()]) {
      return val.toUpperCase();
    }
    // If it's an emoji flag, convert to ISO code
    const isoFromEmoji = getISOFromEmoji(val);
    if (isoFromEmoji && countryCodeMap[isoFromEmoji]) {
      return isoFromEmoji;
    }
    return '';
  };

  // Update selected when value prop changes
  useEffect(() => {
    const isoCode = getISOFromValue(value);
    setSelected(isoCode);
  }, [value]);

  // Auto-select flag when countryName prop matches a country
  useEffect(() => {
    if (countryName) {
      const isoCode = getISOFromCountryName(countryName);
      if (isoCode && isoCode !== selected) {
        setSelected(isoCode);
        const countryFullName = countryCodeMap[isoCode] || '';
        const emojiFlag = getEmojiFlag(isoCode);
        onChange?.(emojiFlag);
        onSelect?.({ code: isoCode, name: countryFullName, flag: emojiFlag });
      }
    }
  }, [countryName]);

  const handleSelect = (countryCode) => {
    setSelected(countryCode);
    const countryName = countryCodeMap[countryCode] || '';
    const emojiFlag = getEmojiFlag(countryCode);
    onChange?.(emojiFlag);
    onSelect?.({ code: countryCode, name: countryName, flag: emojiFlag });
  };

  return (
    <div className="flag-selector-wrapper" style={{ width: '100%' }}>
      <ReactFlagsSelect
        selected={selected}
        onSelect={handleSelect}
        searchable
        searchPlaceholder="Search country..."
        placeholder="Select a country"
        className="flag-select-component"
      />
    </div>
  );
};

export default FlagSelector;
export { countryCodeMap, getEmojiFlag, getISOFromEmoji };
